/**
 * boss-pattern.ts — 「可學習魔王 AI」原型 (老闆 2026-06「魔王要可學習、可精通、有攻防節奏」)
 *
 * 把魔王從「cooldown 到了就隨機放招的彈幕產生器」改成一個**固定循環的攻防狀態機**:
 *
 *   IDLE  (待機 ~0.8s)   → 魔王靜止蓄勢,玩家有喘息空間
 *   TELEGRAPH (預警 ~0.7s) → 出現「該招專屬、可區分」的預警視覺,玩家學會看預警決定怎麼閃
 *   FIRE  (放招,瞬間)    → 在預警結束的那一幀真正射出該招 (justFired=true)
 *   RECOVER (破綻 ~1.2s)  → 魔王明顯「露破綻、不放招」,獎勵讀懂節奏的玩家集中輸出
 *   → 回 IDLE,推進到序列的下一招
 *
 * 為什麼這比舊隨機「可精通」:
 *   - 招式序列**固定可預測** (SHU_MO: 狙擊→狙擊→雷射→循環)。玩家死幾次能「背起來舞步」。
 *   - 每招 FIRE 前都有**夠長 (~0.7s) 的專屬預警** → 看得清、來得及反應 (K-3 鐵則)。
 *   - 每招後都有**破綻窗** → 戰鬥變「攻防舞蹈」而非單方面閃彈。
 *   - berserk 只改「序列內容 / timing 縮短」,**不是改成隨機** → 依然可背、可精通。
 *
 * 設計成純函式 (不依賴 Phaser / 不讀寫修煉值):scene 只持有 BossPatternState、
 * 每幀餵 dtMs,拿回 {phase, justFired, currentMoveId, telegraphProgress, recoverProgress}。
 * timing 與序列確定性可以完全單元測試。學習與遊戲分離:純遊戲機制。
 *
 * 推廣到 FU_HUN / YUAN_SU:只要各自定義一份 BossPatternConfig
 * (sequence + timing,berserkSequence/berserkTiming 可選),用同一個 advanceBossPattern
 * 驅動即可 — 不需碰引擎。見檔尾 makePatternConfig 與 SHU_MO_PATTERN。
 */

/** 攻防循環的四個階段。 */
export type BossPatternPhase = 'IDLE' | 'TELEGRAPH' | 'FIRE' | 'RECOVER';

/**
 * 序列裡的一個「招式 id」。純識別字串 — 引擎不關心它代表什麼招,
 * 由 boss-pool 的 tickAttacks 把 id 翻譯成實際 BossAttack[] (解耦)。
 * SHU_MO 用 'sniper' / 'laser';其它魔王可自訂自己的 id。
 */
export type BossMoveId = string;

/** 每個階段的持續時間 (ms)。FIRE 是瞬間事件,不佔時間 (放完招立刻進 RECOVER)。 */
export interface BossPatternTiming {
  /** 待機時長 ms (魔王靜止蓄勢)。 */
  idleMs: number;
  /** 預警時長 ms (專屬預警視覺顯示;K-3 建議 >= 600)。 */
  telegraphMs: number;
  /** 破綻時長 ms (魔王露破綻、不放招、可被集中輸出)。 */
  recoverMs: number;
}

/** 一隻魔王的完整 pattern 設定:固定序列 + timing。berserk 版本可選。 */
export interface BossPatternConfig {
  /** 固定循環的招式序列 (依序 telegraph→fire,循環到底回頭)。至少 1 招。 */
  sequence: BossMoveId[];
  /** 一般 (phase1/常態) 的階段 timing。 */
  timing: BossPatternTiming;
  /** berserk 時改用的序列 (省略 = 沿用 sequence)。內容換、不是變隨機。 */
  berserkSequence?: BossMoveId[];
  /** berserk 時改用的 timing (省略 = 沿用 timing,通常縮短)。 */
  berserkTiming?: BossPatternTiming;
}

/** 引擎持有的可變狀態。scene 為每隻 boss 持有一份,每幀餵給 advanceBossPattern。 */
export interface BossPatternState {
  phase: BossPatternPhase;
  /** 目前在序列裡的索引 (指向「正在 / 即將」施放的招)。 */
  seqIndex: number;
  /** 目前階段已經過的時間 ms。 */
  elapsedMs: number;
}

/** advanceBossPattern 每幀回傳的快照 (純資料,給 scene 驅動視覺 + 放招)。 */
export interface BossPatternTick {
  /** 進階後的目前階段。 */
  phase: BossPatternPhase;
  /** 目前序列指向的招式 id (TELEGRAPH/FIRE 期間 = 即將/正在放的招)。 */
  currentMoveId: BossMoveId;
  /** 本幀是否剛從 TELEGRAPH→FIRE (恰好放招的那一幀)。scene 看這個來真正 spawn 招式。 */
  justFired: boolean;
  /** TELEGRAPH 進度 0→1 (給預警環 / 瞄準線動畫;非 TELEGRAPH 時為 0)。 */
  telegraphProgress: number;
  /** RECOVER 進度 0→1 (給「破綻」視覺,例如魔王微暗→恢復;非 RECOVER 時為 0)。 */
  recoverProgress: number;
  /** 目前是否處於破綻窗 (= phase==='RECOVER')。scene 可據此給「集中輸出」提示。 */
  vulnerable: boolean;
}

/** 建一個乾淨的初始 state (從 IDLE、序列第 0 招開始)。 */
export function createBossPatternState(): BossPatternState {
  return { phase: 'IDLE', seqIndex: 0, elapsedMs: 0 };
}

/** 小工具:依 berserk 旗選用一般 / berserk 序列與 timing。 */
function activeSequence(cfg: BossPatternConfig, berserk: boolean): BossMoveId[] {
  const seq = berserk && cfg.berserkSequence ? cfg.berserkSequence : cfg.sequence;
  // 防呆:序列不該為空;真空了就退化成單招 'idle-noop' 以免除零。
  return seq.length > 0 ? seq : ['idle-noop'];
}

function activeTiming(cfg: BossPatternConfig, berserk: boolean): BossPatternTiming {
  return berserk && cfg.berserkTiming ? cfg.berserkTiming : cfg.timing;
}

/**
 * 推進魔王 pattern 一幀。**純函式** (除了 mutate 傳入的 state — 刻意,省 GC)。
 *
 * 狀態轉移 (固定、確定性,給定相同輸入永遠相同輸出):
 *   IDLE      → 過了 idleMs      → TELEGRAPH (elapsed 歸零)
 *   TELEGRAPH → 過了 telegraphMs → FIRE      (本幀 justFired=true)
 *   FIRE      → 永遠下一幀立即    → RECOVER   (FIRE 不佔時間,只當「放招事件」)
 *   RECOVER   → 過了 recoverMs   → IDLE,seqIndex 進到序列下一招 (循環)
 *
 * @param state   可變狀態 (會被原地更新)。
 * @param dtMs    本幀毫秒。
 * @param cfg     該魔王的固定序列 + timing。
 * @param berserk 是否 berserk (切換到 berserkSequence/Timing)。
 */
export function advanceBossPattern(
  state: BossPatternState,
  dtMs: number,
  cfg: BossPatternConfig,
  berserk = false,
): BossPatternTick {
  const seq = activeSequence(cfg, berserk);
  const timing = activeTiming(cfg, berserk);
  // 序列長度可能在 berserk 切換時改變 → 夾住 index 避免越界。
  if (state.seqIndex >= seq.length) state.seqIndex = state.seqIndex % seq.length;

  state.elapsedMs += Math.max(0, dtMs);

  let justFired = false;

  // FIRE 是「事件階段」:advanceBossPattern 在某幀把 phase 設成 'FIRE' 並回報
  // justFired=true,但同一次呼叫立即把 state 推進到 RECOVER,讓 FIRE 不佔任何時間。
  // 為了讓「回傳的 tick.phase」精確反映那一幀是放招幀,我們用一個本地旗。
  let firedThisCall = false;

  // 用 while 處理單幀跨越多個短階段的極端情況 (例如 dtMs 很大或 timing 很短)。
  // 但 FIRE 只在 TELEGRAPH 結束時觸發一次,所以最多前進有限步。
  let guard = 0;
  while (guard++ < 8) {
    if (state.phase === 'IDLE') {
      if (state.elapsedMs >= timing.idleMs) {
        state.elapsedMs -= timing.idleMs;
        state.phase = 'TELEGRAPH';
        continue;
      }
      break;
    }
    if (state.phase === 'TELEGRAPH') {
      if (state.elapsedMs >= timing.telegraphMs) {
        state.elapsedMs -= timing.telegraphMs;
        state.phase = 'FIRE';
        firedThisCall = true;
        justFired = true;
        // FIRE 不佔時間:立刻轉 RECOVER。但本次呼叫的回傳 phase 仍報 'FIRE'
        // (見下方回傳),讓 scene 那一幀知道「招剛出去」。
        state.phase = 'RECOVER';
        continue;
      }
      break;
    }
    if (state.phase === 'RECOVER') {
      if (state.elapsedMs >= timing.recoverMs) {
        state.elapsedMs -= timing.recoverMs;
        state.phase = 'IDLE';
        // 推進序列到下一招 (循環)。
        state.seqIndex = (state.seqIndex + 1) % seq.length;
        continue;
      }
      break;
    }
    break;
  }

  const currentMoveId = seq[state.seqIndex] ?? seq[0];

  // 若本幀剛放招,回報的 phase 用 'FIRE' (即使 state 已內部推進到 RECOVER),
  // 讓 scene 的那一幀拿到 justFired + phase==='FIRE' 一致的語意。
  const reportPhase: BossPatternPhase = firedThisCall ? 'FIRE' : state.phase;

  const telegraphProgress =
    state.phase === 'TELEGRAPH' && timing.telegraphMs > 0
      ? Math.min(1, state.elapsedMs / timing.telegraphMs)
      : reportPhase === 'FIRE'
        ? 1
        : 0;

  const recoverProgress =
    state.phase === 'RECOVER' && !firedThisCall && timing.recoverMs > 0
      ? Math.min(1, state.elapsedMs / timing.recoverMs)
      : 0;

  return {
    phase: reportPhase,
    currentMoveId,
    justFired,
    telegraphProgress,
    recoverProgress,
    // 破綻窗 = RECOVER 中 (且不是剛放招那一幀)。
    vulnerable: state.phase === 'RECOVER' && !firedThisCall,
  };
}

// ─── SHU_MO (數魔) 固定循環設定 ──────────────────────────────────────────────
//
// 數魔 = 星算 / 精準爆發 → 招牌【雷射 + 高速狙擊】。
// phase1 序列「狙擊 → 狙擊 → 雷射」固定循環:兩發好預判的狙擊培養玩家節奏感,
// 再來一發招牌雷射當高潮;每招之間的 RECOVER 是固定破綻窗。
//
// boss-phase2-transform (老闆 2026-06「攻擊方式要改變 動態變換」):
//   berserk (=phase2,半血) 不只「加一發狙擊變快」,而是**換一套質變招式組合**:
//   引入兩個 phase1 沒有的招式 id —— 'cross-laser' (雙雷射交叉,新彈幕形)
//   與 'ring' (環形彈幕,全新 kind)。berserk 序列更長、輪替更花:
//     狙擊 → 雙雷交叉 → 狙擊 → 環彈 → 雷射
//   玩家會明顯感覺「數魔換打法了」(招式種類變,不只變密)。
//   每招仍保留專屬預警 + RECOVER 破綻窗 (K-3 鐵則:看得清、躲得過)。

export const SHU_MO_MOVE_SNIPER: BossMoveId = 'sniper';
export const SHU_MO_MOVE_LASER: BossMoveId = 'laser';
/** phase2 限定:雙雷射交叉 (上下兩道分開預警,中央留縫) — phase1 沒有的新形態。 */
export const SHU_MO_MOVE_CROSS_LASER: BossMoveId = 'cross-laser';
/** phase2 限定:環形彈幕 (全新 kind ring-burst,帶缺口可衝) — phase1 沒有的新招。 */
export const SHU_MO_MOVE_RING: BossMoveId = 'ring';

export const SHU_MO_PATTERN: BossPatternConfig = {
  sequence: [SHU_MO_MOVE_SNIPER, SHU_MO_MOVE_SNIPER, SHU_MO_MOVE_LASER],
  timing: {
    idleMs: 800,      // 待機 0.8s — 喘息
    telegraphMs: 700, // 預警 0.7s — K-3 看得清 (老闆鐵則)
    recoverMs: 1200,  // 破綻 1.2s — 明顯,獎勵集中輸出
  },
  // boss-phase2-transform: berserk 換成更長、招式種類更多的序列 (引入 cross-laser + ring),
  // 不只變密;timing 縮短但每招仍有 >=0.6s 預警 + >=0.8s 破綻 (仍固定可背、可躲)。
  berserkSequence: [
    SHU_MO_MOVE_SNIPER,
    SHU_MO_MOVE_CROSS_LASER,
    SHU_MO_MOVE_SNIPER,
    SHU_MO_MOVE_RING,
    SHU_MO_MOVE_LASER,
  ],
  berserkTiming: {
    idleMs: 500,      // 待機縮短
    telegraphMs: 600, // 預警仍 >= 0.6s (不犧牲可學性)
    recoverMs: 900,   // 破綻仍夠長被讀到
  },
};
